// V1 source: customer/midas/components/MidasArtifactPanel.tsx
// Strategy: logic-copied
// Verified no-MUI: yes
//
// V2 dock panel — wraps the active artifact in chrome (header + bordered
// content area) and routes by artifact type. Phase 2A wired the IMAGE viewer
// body. Phase 2B adds diagram_overlay + action_form. Tier B types
// (machine_card, sr_summary, sr_detail, table) land in Phase 2C — see
// `// TODO 2C` switch arms below.
//
// Phase 2B: factored out `MidasArtifactBodyV2` (exported) so the phone full-
// screen takeover can reuse the bordered content + error-boundary chrome
// without duplicating viewer-mounting logic.
//
// Logic copied verbatim from V1 panel:
//   - Reads artifactTabs / activeArtifactTabId / artifactPanelWidth /
//     artifactPanelOpen from midasPage state (selectors unchanged).
//   - Width-only animation (no transform — preserves position:fixed tooltip
//     containing block, F6.4 invariant).
//   - ErrorBoundary around the active artifact viewer with a fallback panel.
//   - Empty state when artifactPanelOpen=true but no active tab.
//
// Visual layer rewritten in Tailwind. Per Paper artboard `MIDAS V2 — Phase
// 2 — Dock — Spec` panels H + I:
//   - Loading: shimmer rectangles (handled inside individual viewers).
//   - Error: layers icon + "Content not loaded" + "RETRY" terracotta link.
//
// Mounting decision (which breakpoints get the dock) is made by
// `MidasPageV2.tsx` — this component just renders chrome when `isOpen`.

import React, {
  Component,
  ErrorInfo,
  ReactNode,
  Suspense,
  lazy,
} from "react";
import { Layers, FileWarning } from "lucide-react";

import { useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { ArtifactTab } from "../../midasPageModels";
import MidasArtifactHeaderV2 from "./MidasArtifactHeaderV2";
import MidasArtifactImageViewerV2 from "./MidasArtifactImageViewerV2";
import MidasArtifactDiagramViewerV2 from "./MidasArtifactDiagramViewerV2";
import MidasArtifactTableRendererV2 from "./MidasArtifactTableRendererV2";
import MidasArtifactSRSummaryV2 from "./MidasArtifactSRSummaryV2";
import MidasArtifactMachineCardV2 from "./MidasArtifactMachineCardV2";
import MidasArtifactSRDetailV2 from "./MidasArtifactSRDetailV2";

// Lazy-load the action form to keep initial bundle small (mirrors V1 pattern)
const MidasArtifactActionFormV2 = lazy(
  () => import("./MidasArtifactActionFormV2")
);

// ===================================================================
// ART-6: Error boundary — catches render errors in artifact viewers
// ===================================================================

interface ArtifactErrorBoundaryProps {
  tab: ArtifactTab;
  translate: (key: string) => string;
  children: ReactNode;
}

interface ArtifactErrorBoundaryState {
  hasError: boolean;
}

class ArtifactErrorBoundary extends Component<
  ArtifactErrorBoundaryProps,
  ArtifactErrorBoundaryState
> {
  state: ArtifactErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(_: Error): ArtifactErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.warn("[ArtifactPanelV2] Render error:", error.message, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return <ArtifactFallback tab={this.props.tab} translate={this.props.translate} />;
    }
    return this.props.children;
  }
}

const ArtifactFallback: React.FC<{ tab: ArtifactTab; translate: (key: string) => string }> = ({
  tab,
  translate,
}) => (
  <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-10">
    <FileWarning
      size={48}
      strokeWidth={1.4}
      className="text-midas-muted-stone opacity-50"
    />
    <div
      className="max-w-[240px] truncate text-center font-sora text-[14px] font-semibold text-midas-graphite"
      style={{ fontFamily: "'Sora', sans-serif" }}
    >
      {tab.artifact.title}
    </div>
    <div className="text-center font-sora text-[13px] text-midas-muted-stone">
      {translate("midas.artifact.contentNotLoaded")}
    </div>
  </div>
);

// ===================================================================
// Type-aware content router — Phase 2A wires only image; Phase 2B + 2C
// extend this switch.
// ===================================================================

const ArtifactContent: React.FC<{ tab: ArtifactTab }> = ({ tab }) => {
  switch (tab.artifact.type) {
    // Phase 2B — diagram_overlay + action_form wired in. Both are active
    // artifact paths today (Round 14: image / diagram_overlay / action_form).
    case "diagram_overlay":
      return <MidasArtifactDiagramViewerV2 key={tab.id} tab={tab} />;
    case "action_form":
      return (
        <Suspense
          fallback={
            <div className="flex flex-1 items-center justify-center p-8">
              <span
                aria-hidden="true"
                className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-midas-warm-stone border-t-midas-terracotta"
              />
            </div>
          }
        >
          <MidasArtifactActionFormV2 key={tab.id} tab={tab} />
        </Suspense>
      );
    // Phase 2C — Tier B forward-compat viewers wired in. Bot does not
    // actively emit these today (per Round 14), but the dock chrome
    // stays consistent for forward-compat. Visual verification done
    // via the dev-only debug harness (MidasDebugHarnessV2 +
    // ?midas-debug-artifact=…).
    case "table":
      return <MidasArtifactTableRendererV2 key={tab.id} tab={tab} />;
    case "machine_card":
      return <MidasArtifactMachineCardV2 key={tab.id} tab={tab} />;
    case "sr_summary":
      return <MidasArtifactSRSummaryV2 key={tab.id} tab={tab} />;
    case "sr_detail":
      return <MidasArtifactSRDetailV2 key={tab.id} tab={tab} />;
    case "platform_file":
    case "image":
    default:
      // Image viewer is the catch-all for image-shaped artifacts and
      // platform_file (which is forward-compat for direct file URLs).
      return <MidasArtifactImageViewerV2 key={tab.id} tab={tab} />;
  }
};

// ===================================================================
// MidasArtifactBodyV2 — exported helper used by both the desktop/tablet
// dock panel AND the phone full-screen takeover. Renders the bordered
// content area with error boundary + content router for the active tab,
// or the empty state if no tab is active. Header is the caller's
// responsibility (the dock + the phone takeover render different chrome).
// ===================================================================

interface MidasArtifactBodyV2Props {
  /** Optional padding around the bordered content card. Defaults to `12px`
   *  (matches V1 dock). Set to `0` for the phone takeover where we want the
   *  body edge-to-edge inside the Dialog. */
  padding?: number | string;
}

export const MidasArtifactBodyV2: React.FC<MidasArtifactBodyV2Props> = ({
  padding = 12,
}) => {
  const { translate } = useTranslation("chatBot");
  const tabs = useAppSelector((state) => state.midasPage.artifactTabs);
  const activeTabId = useAppSelector((state) => state.midasPage.activeArtifactTabId);
  const activeTab = tabs.find((t) => t.id === activeTabId);

  if (activeTab) {
    return (
      <div
        className="flex flex-1 flex-col overflow-hidden"
        style={{ padding }}
      >
        <div className="flex flex-1 flex-col overflow-hidden rounded-md border border-midas-stone">
          <ArtifactErrorBoundary tab={activeTab} translate={translate}>
            <ArtifactContent tab={activeTab} />
          </ArtifactErrorBoundary>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-10">
      <Layers
        size={36}
        strokeWidth={1.4}
        className="text-midas-muted-stone opacity-50"
      />
      <div className="max-w-[280px] text-center font-sora text-[13px] leading-relaxed text-midas-muted-stone">
        {translate("midas.emptyState.noArtifacts")}
      </div>
    </div>
  );
};

// ===================================================================
// MidasArtifactPanelV2
// ===================================================================

interface MidasArtifactPanelV2Props {
  /** F6.7 — propagate user `prefers-reduced-motion` setting if known. */
  prefersReducedMotion?: boolean;
}

const MidasArtifactPanelV2: React.FC<MidasArtifactPanelV2Props> = ({
  prefersReducedMotion = false,
}) => {
  const { translate } = useTranslation("chatBot");
  const panelWidth = useAppSelector((state) => state.midasPage.artifactPanelWidth);
  const isOpen = useAppSelector((state) => state.midasPage.artifactPanelOpen);

  // F6.4 invariant: width-only transition (NEVER transform — would create a
  // new containing block and break position:fixed tooltips).
  const transitionStyle: React.CSSProperties = prefersReducedMotion
    ? { transition: "none" }
    : { transition: "width 0.28s ease-out, min-width 0.28s ease-out" };

  return (
    <aside
      aria-label={translate("midas.aria.artifactPanel") || "Artifact viewer panel"}
      aria-hidden={!isOpen}
      style={{
        width: isOpen ? `${panelWidth}px` : 0,
        minWidth: isOpen ? "360px" : 0,
        ...transitionStyle,
      }}
      className={[
        "flex h-full flex-shrink-0 flex-col overflow-hidden bg-midas-bone-light",
        isOpen ? "border-l border-midas-stone" : "",
      ].join(" ")}
    >
      <MidasArtifactHeaderV2 />
      <MidasArtifactBodyV2 padding={12} />
    </aside>
  );
};

export default MidasArtifactPanelV2;
